// The planisphere drawn over the home's own stars when the Horoscope is open
// (Figma Sky / Planisphere): hour lines from a pole above the screen, every
// 10° and bolder every 30°, circles of declination every 10°, a dashed
// ecliptic, small hour and degree marks, and the Milky Way as faint dots on
// the days that have one. The geometry is the Figma frame's (1440 × 900),
// fitted to the height and centred, as the sky is.
import { memo, useMemo } from 'react';
import type { Weekday } from '../../systems/api/types';
import { seededRandom, WEEKDAY_ORDER } from './weekdaySkies';

/** The design frame the chart was drawn in. */
const FRAME_H = 900;
/** The pole, above the frame's top centre. */
const POLE_Y = -380;
/** Circles of declination: the first radius and the step (10° each); 0° falls at 1420. */
const CIRCLE_FIRST = 520;
const CIRCLE_STEP = 150;
const CIRCLES = 11;
const DEC_AT_FIRST = 60;
/** Hour lines start this far from the pole. */
const HOUR_INNER = 160;
/** The ecliptic: a great circle drawn as a circle centred below and right of the frame. */
const ECLIPTIC = { dx: 260, y: 2050, r: 1820 };
const ECLIPTIC_LABEL = { dx: 476, y: 309 };
/** Milky Way dots in the full frame. */
const MILKY_WAY_DOTS = 520;

interface Props {
  width: number;
  height: number;
  weekday: Weekday;
  /** The day's Milky Way, 0 for none. */
  milkyWay: number;
}

const ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V', 'VI'];

export const Planisphere = memo(function Planisphere({ width: W, height: H, weekday, milkyWay }: Props) {
  const chart = useMemo(() => drawChart(W, H, weekday, milkyWay), [W, H, weekday, milkyWay]);
  return (
    <svg className="planisphere" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <path className="chart-hours" d={chart.hours} />
      <path className="chart-hours-bold" d={chart.boldHours} />
      <path className="chart-circles" d={chart.circles} />
      <circle className="chart-ecliptic" cx={chart.ecliptic.cx} cy={chart.ecliptic.cy} r={chart.ecliptic.r} />
      {chart.milkyWay.length > 0 && (
        <g className="chart-milky-way">
          {chart.milkyWay.map((d, i) => (
            <circle key={i} cx={d.x} cy={d.y} r={d.r} opacity={d.o} />
          ))}
        </g>
      )}
      <g className="chart-marks">
        {chart.hourMarks.map((m) => (
          <text key={m.label} x={m.x} y={m.y} className="chart-mark chart-mark-hour">
            {m.label}
          </text>
        ))}
        {chart.decMarks.map((m) => (
          <text key={m.label} x={m.x} y={m.y} className="chart-mark">
            {m.label}
          </text>
        ))}
        {chart.eclipticMark && (
          <text x={chart.eclipticMark.x} y={chart.eclipticMark.y} className="chart-mark chart-mark-ecliptic">
            ECLIPTIC
          </text>
        )}
      </g>
    </svg>
  );
});

interface Mark {
  label: string;
  x: number;
  y: number;
}

function drawChart(W: number, H: number, weekday: Weekday, milkyWay: number) {
  const s = H / FRAME_H;
  const cx = W / 2;
  const cy = POLE_Y * s;
  // Long enough to leave the screen from the pole in any direction.
  const reach = Math.hypot(Math.max(cx, W - cx), H - cy) + 40;
  const f = (n: number) => Math.round(n * 10) / 10;

  let hours = '';
  let boldHours = '';
  const hourMarks: Mark[] = [];
  // Angles measured from straight down; only the lower half ever reaches the screen.
  for (let deg = -90; deg <= 90; deg += 10) {
    const a = (deg * Math.PI) / 180;
    const dx = Math.sin(a);
    const dy = Math.cos(a);
    const seg = `M${f(cx + HOUR_INNER * s * dx)} ${f(cy + HOUR_INNER * s * dy)}L${f(cx + reach * dx)} ${f(cy + reach * dy)}`;
    if (deg % 30 === 0) {
      boldHours += seg;
      // The hour label where the line crosses near the top: IIIh on the meridian, rising to the left.
      const hour = 3 - deg / 30;
      const y = 43 * s;
      const x = cx + (y - cy) * Math.tan(a);
      if (Math.abs(deg) < 90 && hour >= 0 && hour < ROMAN.length && x > 8 && x < W - 30) {
        hourMarks.push({ label: `${ROMAN[hour]}h`, x: f(x + 6), y: f(y) });
      }
    } else {
      hours += seg;
    }
  }

  let circles = '';
  const decMarks: Mark[] = [];
  for (let i = 0; i < CIRCLES; i++) {
    const r = (CIRCLE_FIRST + CIRCLE_STEP * i) * s;
    circles += `M${f(cx - r)} ${f(cy)}a${f(r)} ${f(r)} 0 1 0 ${f(2 * r)} 0a${f(r)} ${f(r)} 0 1 0 ${f(-2 * r)} 0`;
    const y = cy + r - 6;
    const dec = DEC_AT_FIRST - 10 * i;
    if (dec <= 40 && y > 70 && y < H - 4) decMarks.push({ label: `${dec > 0 ? '+' : ''}${dec}°`, x: f(cx + 8), y: f(y) });
  }

  const ecliptic = { cx: f(cx + ECLIPTIC.dx * s), cy: f(ECLIPTIC.y * s), r: f(ECLIPTIC.r * s) };
  const ex = cx + ECLIPTIC_LABEL.dx * s;
  const eclipticMark = ex < W - 60 ? { x: f(ex), y: f(ECLIPTIC_LABEL.y * s) } : null;

  const dots: Array<{ x: number; y: number; r: number; o: number }> = [];
  if (milkyWay > 0) {
    // The band where the home's Milky Way glows (sky.css): wide, tilted 18° up to the right.
    const rand = seededRandom(WEEKDAY_ORDER.indexOf(weekday) * 104729 + 31);
    const count = Math.round(MILKY_WAY_DOTS * Math.min(2.5, (W * H) / (1440 * FRAME_H)));
    const bandCx = W / 2;
    const bandCy = 0.43 * H;
    const half = 0.7 * W;
    const tilt = (-18 * Math.PI) / 180;
    for (let i = 0; i < count; i++) {
      const u = (rand() * 2 - 1) * half;
      // Denser along the middle of the band.
      const v = (rand() + rand() + rand() - 1.5) * 0.16 * H;
      const x = bandCx + u * Math.cos(tilt) - v * Math.sin(tilt);
      const y = bandCy + u * Math.sin(tilt) + v * Math.cos(tilt);
      if (x < -2 || x > W + 2 || y < -2 || y > H + 2) continue;
      dots.push({ x: f(x), y: f(y), r: f(0.5 + rand() * 0.8), o: f(0.35 + rand() * 0.65) });
    }
  }

  return { hours, boldHours, circles, ecliptic, eclipticMark, hourMarks, decMarks, milkyWay: dots };
}
